import { trackUrl } from '../../components/Correspondence';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getToken, API_BASE, photoUrl } from '../../services/apiClient';
import { VendorPicker } from './CreatePoModal';
import AttachButton from '../../components/AttachButton';

export const PO_STATUSES = ['Hold For Release', 'Released', 'Ship Date Confirmed', 'Received'];
export const PO_STATUS_COLORS = {
  'Hold For Release':    { bg: '#fef3c7', text: '#92400e' },
  'Released':            { bg: '#dbeafe', text: '#1e40af' },
  'Ship Date Confirmed': { bg: '#e0e7ff', text: '#4338ca' },
  'Received':            { bg: '#dcfce7', text: '#166534' },
};

// How a PO's material is getting here, in one line (Ryan, Oct 12 2026 — the PO list didn't show the
// tracking or the shop pickup he'd added): 🚚 carrier + tracking with Track it ↗, 🛻 the pickup /
// delivery shop order written from the PO (opens it), 🏭 when the shop was told it's on the way.
const SHOP_WORK = { new: 'requested', not_started: 'requested', in_progress: 'in progress', scheduled: 'scheduled', done: 'done', complete: 'done', completed: 'done' };
const shortDay = s => { const d = new Date(s); return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
export function PoShipBits({ po, compact = false }) {
  const navigate = useNavigate();
  const so = po.shop_order;
  if (!po.tracking_number && !so && !po.shop_told_at) return null;
  const chip = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: compact ? 11.5 : 12, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: compact ? 4 : 0 }} onClick={e => e.stopPropagation()}>
      {po.tracking_number && (
        <span style={{ ...chip, background: '#eff6ff', color: '#1e3a5f', border: '1px solid #bfdbfe' }} title={po.tracking_note || ''}>
          🚚 {po.tracking_carrier ? `${po.tracking_carrier} ` : ''}{po.tracking_number}
          <a href={trackUrl(po.tracking_number, po.tracking_carrier)} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', textDecoration: 'none', marginLeft: 2 }}>Track ↗</a>
        </span>
      )}
      {so && (
        <button type="button" onClick={() => navigate(`/admin/shop?tab=shop_order&order=${so.id}`)} title={so.title || 'Shop order'}
          style={{ ...chip, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', cursor: 'pointer', fontFamily: 'inherit' }}>
          🛻 Shop pickup · {SHOP_WORK[so.work_status] || so.work_status || 'ordered'}{so.needed_by ? ` · ${shortDay(so.needed_by)}` : ''}
        </button>
      )}
      {po.shop_told_at && <span style={{ ...chip, background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' }}>🏭 Shop told it’s on the way · {shortDay(po.shop_told_at)}</span>}
    </div>
  );
}

const SHOP_ADDRESS ='Titan Electrical Contracting\nSuite 100, 5450 Mill Street\nReno, NV 89502';

// Full PO detail/edit modal shared by the Purchase Orders page and each job's
// PO section: edit fields + status, pick a ship-to, attach a vendor quote,
// print the PO (quote merged behind), and spin up a pickup/delivery shop order.
export default function PoDetailModal({ po, vendors = [], onClose, onSaved, onDeleted }) {
  const navigate = useNavigate();
  // Deleting a PO removes a financial record — Executives/admin only.
  const myRole = (() => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role; } catch { return null; } })();
  const canDelete = ['admin', 'Admin', 'Executive'].includes(myRole);
  // Renumbering is how a duplicate gets fixed (two #20868s, Sep 30 2026) — office, Executives, owner.
  const canRenumber = ['admin', 'Admin', 'Executive', 'Accounting'].includes(myRole);
  const [numEdit, setNumEdit] = useState(null);   // the new number being typed, or null
  const [numBusy, setNumBusy] = useState(false);
  async function renumber() {
    const n = String(numEdit || '').trim();
    if (!n || n === String(po.po_number)) { setNumEdit(null); return; }
    setNumBusy(true); setErr('');
    try {
      await apiFetch(`/purchase-orders/${po.id}`, { method: 'PATCH', body: JSON.stringify({ po_number: n }) });
      setNumEdit(null); onSaved && onSaved();
    } catch (e) {
      let msg = e?.error || e?.message || 'Could not change the number.';
      try { const j = JSON.parse(e?.message || ''); if (j && j.error) msg = j.error; } catch { /* plain text */ }
      setErr(msg); window.alert(msg);
    }
    setNumBusy(false);
  }
  const [form, setForm] = useState({
    description: po.description || '',
    vendor: po.vendor || '',
    cost: po.cost != null ? String(po.cost) : '',
    notes: po.notes || '',
    status: po.status || PO_STATUSES[0],
    ship_to: po.ship_to || '',
    is_rental: !!po.is_rental,
    rental_start: po.rental_start || '',
    ship_date: po.ship_date || '',
    notify_shop: !!po.notify_shop,
    is_major: !!po.is_major,
    ndot_certs: !!po.ndot_certs,
    project_id: po.project_id || '',
    tracking_number: po.tracking_number || '',
    tracking_carrier: po.tracking_carrier || '',
  });
  const [quote, setQuote] = useState({ filename: po.vendor_quote_filename || null, name: po.vendor_quote_original_name || null });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    apiFetch('/projects').then(p => setProjects(Array.isArray(p) ? p : [])).catch(() => {});
  }, []);
  // Released in parts on a submittal (Ryan, Oct 26 2026): Part A, B, C… and where each stands.
  const [parts, setParts] = useState([]);
  useEffect(() => { if (po && po.id) apiFetch(`/purchase-orders/${po.id}/submittal-parts`).then(r => setParts(Array.isArray(r) ? r : [])).catch(() => {}); }, [po && po.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.description.trim()) { setErr('Description is required.'); return; }
    setSaving(true); setErr('');
    try {
      await apiFetch(`/purchase-orders/${po.id}`, { method: 'PATCH', body: JSON.stringify({
        description: form.description.trim(), vendor: form.vendor.trim() || null,
        cost: form.cost !== '' ? parseFloat(form.cost) : null, notes: form.notes.trim() || null,
        status: form.status, ship_to: form.ship_to.trim() || null,
        is_rental: form.is_rental ? 1 : 0, rental_start: form.is_rental ? (form.rental_start || null) : null,
        ship_date: form.ship_date || null, notify_shop: form.notify_shop ? 1 : 0,
        is_major: form.is_major ? 1 : 0,
        ndot_certs: form.is_major && form.ndot_certs ? 1 : 0,
        project_id: form.project_id || null,
        tracking_number: form.tracking_number.trim() || null, tracking_carrier: form.tracking_carrier.trim() || null,
      }) });
      const proj = projects.find(p => p.id === form.project_id);
      onSaved && onSaved({ ...po, ...form, cost: form.cost !== '' ? parseFloat(form.cost) : null, vendor: form.vendor.trim() || null, ship_to: form.ship_to.trim() || null, vendor_quote_filename: quote.filename, vendor_quote_original_name: quote.name,
        project_id: form.project_id || null, project_name: proj ? proj.name : (form.project_id ? po.project_name : null), project_number: proj ? proj.project_number : (form.project_id ? po.project_number : null) });
      onClose();
    } catch (e) { setErr(e?.error || e?.message || 'Failed to save.'); }
    setSaving(false);
  }

  async function del() {
    if (!window.confirm(`Delete PO #${po.po_number}? This voids it for good and can't be undone.`)) return;
    setBusy('delete'); setErr('');
    try {
      await apiFetch(`/purchase-orders/${po.id}`, { method: 'DELETE' });
      if (onDeleted) onDeleted(po);
      onClose();
    } catch (e) { setErr(e?.error || e?.message || 'Could not delete the PO.'); setBusy(''); }
  }

  async function uploadQuote(file) {
    if (!file) return;
    setBusy('upload'); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API_BASE}/purchase-orders/${po.id}/vendor-quote`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const updated = await res.json();
      setQuote({ filename: updated.vendor_quote_filename, name: updated.vendor_quote_original_name });
    } catch (e) { setErr('Could not attach the vendor quote.'); }
    setBusy('');
  }
  async function removeQuote() {
    setBusy('remove');
    try { await apiFetch(`/purchase-orders/${po.id}/vendor-quote`, { method: 'DELETE' }); setQuote({ filename: null, name: null }); } catch {}
    setBusy('');
  }

  async function printPO() {
    setBusy('print'); setErr('');
    try {
      const res = await fetch(`${API_BASE}/purchase-orders/${po.id}/pdf`, { headers: { 'x-worker-token': getToken() } });
      if (!res.ok) throw new Error('PDF failed');
      const url = URL.createObjectURL(await res.blob());
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setErr('Could not generate the PO PDF.'); }
    setBusy('');
  }

  async function writeShopOrder() {
    setBusy('so'); setErr('');
    try {
      const created = await apiFetch('/shop-orders', { method: 'POST', body: JSON.stringify({
        type: 'deliver_materials',
        title: `Pick up & deliver — PO #${po.po_number}${po.vendor ? ` (${po.vendor})` : ''}`,
        project_id: po.project_id, priority: 'normal',
        details: { source: 'Vendor', vendor: po.vendor || '', items: form.description || po.description || '', deliver_to: form.ship_to || po.job_location || '', po_number: po.po_number },
      }) });
      navigate(`/admin/shop?tab=shop_order&order=${created.id}`);
    } catch (e) { setErr(e?.error || e?.message || 'Could not create shop order.'); }
    setBusy('');
  }

  const lbl = { fontSize: 11, fontWeight: 700, color: 'var(--gray-muted,#6b7280)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 };
  const chip = (label, onClick) => <button type="button" onClick={onClick} style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f8fafc', color: '#374151', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{label}</button>;

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <div>
            <div style={{ fontSize: 11, color: 'var(--gray-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Purchase Order</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {numEdit === null ? (
                <h3 style={{ margin: 0, fontSize: 20, color: 'var(--orange)' }}>#{po.po_number}</h3>
              ) : (
                <input autoFocus value={numEdit} onChange={e => setNumEdit(e.target.value.replace(/\D/g, ''))} disabled={numBusy}
                  onKeyDown={e => { if (e.key === 'Enter') renumber(); if (e.key === 'Escape') setNumEdit(null); }} onBlur={renumber}
                  style={{ width: 110, fontSize: 20, fontWeight: 800, color: 'var(--orange)', border: '1.5px solid var(--orange)', borderRadius: 7, padding: '2px 8px', fontFamily: 'inherit' }} />
              )}
              {canRenumber && numEdit === null && (
                <button type="button" onClick={() => setNumEdit(String(po.po_number || ''))} title="Change the PO number (e.g. to fix a duplicate)"
                  style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 14, cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}>✎</button>
              )}
              {!!form.is_major && <span style={{ fontSize: 11, fontWeight: 800, background: '#fee2e2', color: '#b91c1c', padding: '2px 8px', borderRadius: 20, letterSpacing: 0.3 }}>⭐ MAJOR</span>}
              {!!form.is_major && !!form.ndot_certs && <span style={{ fontSize: 11, fontWeight: 800, background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 20, letterSpacing: 0.3 }}>NDOT CERTS</span>}
            </div>
            {po.project_name && <div style={{ fontSize: 13, color: 'var(--gray-muted)', marginTop: 2 }}>{[po.project_number, po.project_name].filter(Boolean).join(' — ')}</div>}
            {(po.equipment_asset || po.equipment_name) && <div style={{ fontSize: 12, color: '#9a3412', fontWeight: 700, marginTop: 2 }}>🔧 Charged to {po.equipment_asset || po.equipment_name}</div>}
          </div>
          <button className="admin-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="admin-modal-form" style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 24px 24px' }}>
          {/* Action row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={printPO} disabled={!!busy} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #1e3a5f', background: '#eff6ff', color: '#1e3a5f', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{busy === 'print' ? 'Generating…' : '🖨 Print PO'}</button>
            <button onClick={writeShopOrder} disabled={!!busy || !po.project_id} title={!po.project_id ? 'PO has no job' : 'Create a pickup & delivery shop order'} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: !po.project_id ? '#cbd5e1' : '#1e3a5f', color: '#fff', fontSize: 12, fontWeight: 700, cursor: !po.project_id ? 'not-allowed' : 'pointer' }}>📦 Write Shop Order</button>
          </div>

          {/* Which job this PO is charged to — change it here if it was written
              to the wrong project. */}
          <div className="field-group">
            <label style={lbl}>Job / Project</label>
            <select className="field-input" value={form.project_id} onChange={e => set('project_id', e.target.value)}>
              <option value="">— Unassigned —</option>
              {[...projects]
                .sort((a, b) => String(b.project_number || '').localeCompare(String(a.project_number || ''), undefined, { numeric: true }))
                .map(p => <option key={p.id} value={p.id}>{[p.project_number, p.name].filter(Boolean).join(' — ')}</option>)}
            </select>
            {form.project_id !== (po.project_id || '') && (
              <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>This PO will be re-charged to the selected job when you save.</span>
            )}
          </div>

          <div className="field-group">
            <label style={lbl}>Description</label>
            <textarea className="field-input" rows={3} style={{ resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          <div className="admin-form-row" style={{ display: 'flex', gap: 12 }}>
            <div className="field-group" style={{ flex: 1 }}>
              <label style={lbl}>Vendor</label>
              <VendorPicker value={form.vendor} vendors={vendors} onChange={v => set('vendor', v)} />
            </div>
            <div className="field-group" style={{ width: 130 }}>
              <label style={lbl}>Cost ($)</label>
              <input className="field-input" type="text" inputMode="decimal" placeholder="0.00" value={form.cost} onChange={e => set('cost', e.target.value)} />
            </div>
          </div>
          <div className="admin-form-row" style={{ display: 'flex', gap: 12 }}>
            <div className="field-group" style={{ flex: 1 }}>
              <label style={lbl}>Status</label>
              <select className="field-input" value={form.status} onChange={e => set('status', e.target.value)}>
                {PO_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="field-group" style={{ width: 150 }}>
              <label style={lbl}>Ship date</label>
              <input className="field-input" type="date" value={form.ship_date ? String(form.ship_date).slice(0, 10) : ''} onChange={e => set('ship_date', e.target.value)} />
            </div>
          </div>
          {/* Tracking — from a vendor's email (Correspondence, Oct 10) or typed here (Ryan, Oct 12 2026). */}
          <div className="admin-form-row" style={{ display: 'flex', gap: 12 }}>
            <div className="field-group" style={{ flex: 1 }}>
              <label style={lbl}>Tracking #</label>
              <input className="field-input" value={form.tracking_number} onChange={e => set('tracking_number', e.target.value)} placeholder="e.g. 1Z999AA10123456784" />
            </div>
            <div className="field-group" style={{ width: 150 }}>
              <label style={lbl}>Carrier</label>
              <input className="field-input" value={form.tracking_carrier} onChange={e => set('tracking_carrier', e.target.value)} placeholder="UPS, FedEx, freight…" list="po-carriers" />
              <datalist id="po-carriers">{['UPS', 'FedEx', 'USPS', 'Oak Harbor', 'Estes', 'Old Dominion', 'XPO', 'Vendor truck', 'Will call'].map(c => <option key={c} value={c} />)}</datalist>
            </div>
          </div>
          {(form.tracking_number.trim() || po.shop_order || po.shop_told_at) && (
            <div style={{ marginTop: -4, marginBottom: 10 }}><PoShipBits po={{ ...po, tracking_number: form.tracking_number.trim(), tracking_carrier: form.tracking_carrier.trim() }} /></div>
          )}
          {po.supplier_updated_at && <SupplierStatus po={po} />}
          {parts.length > 0 && (
            <div style={{ border: '1px solid #ddd6fe', background: '#fcfaff', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#5b21b6', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Released in parts</div>
              {parts.map(pt => {
                const d = s => { if (!s) return ''; const x = new Date(String(s).slice(0, 10) + 'T12:00:00'); return isNaN(x) ? s : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
                const where = pt.received_date ? `📦 received ${d(pt.received_date)}` : pt.released_date ? `🚚 released ${d(pt.released_date)}${pt.ship_date ? ' · ships ' + d(pt.ship_date) : ''}` : ['approved', 'approved_with_conditions'].includes(pt.status) ? '✅ approved · holding' : pt.status === 'approved_no_action' ? '✔ no further action' : pt.status === 'revise_resubmit' ? '↩ revise & resubmit' : pt.status === 'submitted' ? '📤 in review' : '⏳ not submitted';
                return (
                  <div key={pt.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '3px 0' }}>
                    <b style={{ color: '#6d28d9', whiteSpace: 'nowrap' }}>Part {pt.letter}</b>
                    <span style={{ flex: 1, minWidth: 0, color: '#374151' }}>{pt.description || pt.category}</span>
                    <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>{where}{pt.tracking_number ? ` · ${pt.tracking_carrier || ''} ${pt.tracking_number}` : ''}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="field-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
              <input type="checkbox" checked={form.notify_shop} onChange={e => set('notify_shop', e.target.checked)} />
              Notify the shop when a ship date is set
            </label>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>For big, coordination-heavy orders. The shop gets an alert when a ship date is entered.</span>
          </div>

          {/* Ship To */}
          <div className="field-group">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={lbl}>Ship To</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {chip('Job site', () => set('ship_to', po.job_location || po.project_name || ''))}
                {chip('Shop / Yard', () => set('ship_to', SHOP_ADDRESS))}
                {chip('Clear', () => set('ship_to', ''))}
              </div>
            </div>
            <textarea className="field-input" rows={3} style={{ resize: 'vertical' }} placeholder="Where should the vendor ship / deliver?" value={form.ship_to} onChange={e => set('ship_to', e.target.value)} />
          </div>

          {/* Rented equipment */}
          <div className="field-group">
            <div style={{ border: '1px solid #ede9fe', background: '#faf5ff', borderRadius: 8, padding: '10px 12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#6d28d9' }}>
                <input type="checkbox" checked={form.is_rental} onChange={e => set('is_rental', e.target.checked)} /> This PO is for rented equipment
              </label>
              {form.is_rental && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ ...lbl, display: 'block' }}>Rental start date</label>
                  <input className="field-input" type="date" value={form.rental_start ? String(form.rental_start).slice(0, 10) : ''} onChange={e => set('rental_start', e.target.value)} />
                  <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 4 }}>Tracked under Shop → Equipment → Rented Equipment.</div>
                </div>
              )}
            </div>
          </div>

          {/* Major PO */}
          <div className="field-group">
            <div style={{ border: '1px solid #fee2e2', background: '#fef2f2', borderRadius: 8, padding: '10px 12px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>
                <input type="checkbox" checked={form.is_major} onChange={e => set('is_major', e.target.checked)} style={{ marginTop: 2 }} />
                <span>This is a Major PO<br /><span style={{ fontSize: 11, fontWeight: 500, color: '#ef4444' }}>Big, higher-dollar orders with multiple ship dates — tracked & filtered separately.</span></span>
              </label>
              {form.is_major && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#b91c1c', marginTop: 10 }}>
                  <input type="checkbox" checked={form.ndot_certs} onChange={e => set('ndot_certs', e.target.checked)} style={{ marginTop: 2 }} />
                  <span>NDOT certifications required<br /><span style={{ fontSize: 11, fontWeight: 500, color: '#ef4444' }}>Certs / Buy America paperwork has to come with this material.</span></span>
                </label>
              )}
            </div>
          </div>

          {/* Vendor quote attachment */}
          <div className="field-group">
            <label style={lbl}>Vendor Quote (prints behind the PO)</label>
            {quote.filename ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {quote.name || 'Vendor quote'}</span>
                <a href={photoUrl(quote.filename)} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>View</a>
                <button onClick={removeQuote} disabled={!!busy} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
              </div>
            ) : (
              // Drag the vendor quote (PDF or image) straight onto here, or click to browse.
              <AttachButton accept="both" icon="📎" disabled={busy === 'upload'}
                label={busy === 'upload' ? 'Uploading…' : 'Drag & drop the vendor quote here, or click to attach'}
                style={{ width: '100%', padding: '15px 12px', fontSize: 13, borderRadius: 8 }}
                onFiles={fs => uploadQuote(fs[0])} />
            )}
          </div>

          <div className="field-group">
            <label style={lbl}>Notes</label>
            <textarea className="field-input" rows={2} style={{ resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>

          {err && <div style={{ color: 'var(--red,#dc2626)', fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 2, alignItems: 'center' }}>
            <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1.5px solid var(--border)', background: '#fff', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: saving ? '#ccc' : 'var(--orange)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
          {canDelete && (
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={del} disabled={!!busy} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', fontSize: 13, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer' }}>
                {busy === 'delete' ? 'Deleting…' : '🗑 Delete PO'}
              </button>
              <span style={{ fontSize: 11.5, color: 'var(--gray-muted,#9ca3af)' }}>Voids this PO for good. The number is retired (not reused).</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// What the supplier says about this PO — Wedco's weekly update, as a PM OK'd it (Oct 4 2026).
// Read-only: the next week's OK replaces it.
function SupplierStatus({ po }) {
  const [open, setOpen] = React.useState(false);
  let lines = []; try { lines = JSON.parse(po.supplier_lines || '[]'); } catch { lines = []; }
  const nice = d => { if (!d) return ''; const x = new Date(String(d).slice(0, 10) + 'T12:00:00'); return isNaN(x) ? String(d) : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: x.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' }); };
  const total = po.supplier_lines_total || 0, shipped = po.supplier_lines_shipped || 0;
  return (
    <div style={{ border: '1px solid #c7d2fe', background: '#f8faff', borderRadius: 10, padding: '10px 12px', margin: '4px 0 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: '#3730a3' }}>📦 Wedco says</span>
        {total > 0 && <span style={{ fontSize: 12.5, fontWeight: 700 }}>{shipped >= total ? 'all shipped' : `${shipped} of ${total} lines shipped`}</span>}
        {po.supplier_est_first && <span style={{ fontSize: 12.5, color: '#374151' }}>{po.supplier_est_first === po.supplier_est_last ? `ships ${nice(po.supplier_est_first)}` : `lines ship ${nice(po.supplier_est_first)} → ${nice(po.supplier_est_last)}`}</span>}
        {po.supplier_hfr > 0 && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '1px 8px' }}>{po.supplier_hfr} on hold for release</span>}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: '#9ca3af' }}>{po.supplier_so ? `Wedco ${String(po.supplier_so).split(',').join(', ')} · ` : ''}updated {nice(po.supplier_updated_at)}</span>
      </div>
      {po.supplier_tracking && <div style={{ fontSize: 12.5, color: '#374151', marginTop: 4 }}>🚚 {po.supplier_tracking}</div>}
      {po.supplier_note && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, whiteSpace: 'pre-wrap' }}>{po.supplier_note}</div>}
      {lines.length > 0 && <button type="button" onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', padding: '4px 0 0', color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{open ? 'Hide the lines' : `Show the ${lines.length} line${lines.length === 1 ? '' : 's'}`}</button>}
      {open && (
        <div style={{ overflowX: 'auto', marginTop: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <tbody>{lines.map((l, i) => (
              <tr key={i} style={{ borderTop: '1px solid #e0e7ff' }}>
                <td style={{ padding: '3px 5px', whiteSpace: 'nowrap' }}>{l.qty != null ? l.qty : (l.qty_text || '')}</td>
                <td style={{ padding: '3px 5px' }}>{l.catalog}</td>
                <td style={{ padding: '3px 5px', whiteSpace: 'nowrap' }}>{l.est_dates && l.est_dates.length > 1 ? l.est_dates.map(nice).join(', ') : (l.est ? nice(l.est) : (l.est_text || ''))}</td>
                <td style={{ padding: '3px 5px', whiteSpace: 'nowrap', color: l.shipped ? '#15803d' : '#9ca3af' }}>{l.shipped ? `shipped ${nice(l.shipped)}` : '—'}</td>
              </tr>))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
